import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, Bell, Check, ChevronDown, ChevronUp, Info, Megaphone } from "lucide-react";
import { api } from "../../api";

const PRIORITY_STYLES = {
  normal: {
    shell: "border-slate-200 bg-white",
    badge: "bg-slate-100 text-slate-700",
    icon: Info,
  },
  important: {
    shell: "border-amber-200 bg-amber-50",
    badge: "bg-amber-100 text-amber-800",
    icon: Bell,
  },
  urgent: {
    shell: "border-rose-200 bg-rose-50",
    badge: "bg-rose-100 text-rose-800",
    icon: AlertTriangle,
  },
};

export default function SellerCommunicationBanner() {
  const [rows, setRows] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [busyId, setBusyId] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/vendors/me/communications", { params: { limit: 50 } });
      setRows(data?.communications || []);
      setUnreadCount(Number(data?.unread_count) || 0);
    } catch (_error) {
      // Seller communications are supplemental; a transient fetch failure must
      // never block the seller workspace or order handling.
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const safeLoad = async () => {
      if (!cancelled) await load();
    };
    safeLoad();
    const timer = window.setInterval(safeLoad, 60000);
    const onVisibility = () => { if (!document.hidden) safeLoad(); };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [load]);

  const unreadRows = useMemo(() => rows.filter((row) => !row.read_at), [rows]);
  const featured = unreadRows[0] || rows[0];

  const markRead = async (id) => {
    if (!id || busyId) return;
    setBusyId(id);
    try {
      await api.post(`/vendors/me/communications/${id}/read`);
      setRows((current) => current.map((row) => (
        row.id === id ? { ...row, read_at: new Date().toISOString() } : row
      )));
      setUnreadCount((count) => Math.max(0, count - 1));
    } catch (_error) {
      // Keep the item unread; the next refresh can safely retry.
    } finally {
      setBusyId("");
    }
  };

  if (!featured) return null;

  const featuredStyle = PRIORITY_STYLES[featured.priority] || PRIORITY_STYLES.normal;
  const FeaturedIcon = featuredStyle.icon;

  return (
    <section className="border-b border-[var(--ap-border)] bg-white/95 px-4 py-3 backdrop-blur sm:px-6 lg:px-8" data-testid="seller-communication-inbox" aria-label="Messages from Arctic Plate">
      <div className="mx-auto max-w-[1480px]">
        <div className={`rounded-2xl border p-4 shadow-sm ${featuredStyle.shell}`}>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex min-w-0 items-start gap-3">
              <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#1F2621] text-white">
                <FeaturedIcon size={17} aria-hidden="true" />
              </div>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#D95A00]">Message from Arctic Plate</p>
                  {unreadCount > 0 && <span className="rounded-full bg-[#D95A00] px-2 py-0.5 text-[10px] font-bold text-white">{unreadCount} unread</span>}
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.12em] ${featuredStyle.badge}`}>{featured.priority || "normal"}</span>
                </div>
                <h2 className="mt-1 truncate text-sm font-semibold text-[#1F2621] sm:text-base">{featured.title}</h2>
                <p className="mt-1 line-clamp-2 text-sm leading-6 text-[var(--ap-ink-soft)]">{featured.message}</p>
              </div>
            </div>
            <div className="flex shrink-0 flex-wrap gap-2 sm:justify-end">
              {!featured.read_at && (
                <button type="button" onClick={() => markRead(featured.id)} disabled={busyId === featured.id} className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl border border-[#D1D5DB] bg-white px-3 py-2 text-xs font-semibold text-[#1F2621] disabled:opacity-50" data-testid="seller-communication-mark-featured-read">
                  <Check size={14} aria-hidden="true" /> {busyId === featured.id ? "Saving…" : "Mark read"}
                </button>
              )}
              <button type="button" onClick={() => setOpen((value) => !value)} className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl bg-[#1F2621] px-3 py-2 text-xs font-semibold text-white" data-testid="seller-communication-toggle" aria-expanded={open}>
                <Megaphone size={14} aria-hidden="true" /> {open ? "Hide messages" : "View messages"} {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </button>
            </div>
          </div>

          {open && (
            <div className="mt-4 grid gap-3 border-t border-black/10 pt-4 lg:grid-cols-2" data-testid="seller-communication-list">
              {rows.map((row) => {
                const style = PRIORITY_STYLES[row.priority] || PRIORITY_STYLES.normal;
                const RowIcon = style.icon;
                return (
                  <article key={row.id} className={`rounded-2xl border p-4 ${style.shell} ${row.read_at ? "opacity-75" : "ring-2 ring-[#FFF3E8]"}`} data-testid={`seller-communication-${row.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex min-w-0 items-start gap-2.5">
                        <RowIcon size={16} className="mt-0.5 shrink-0 text-[#D95A00]" aria-hidden="true" />
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.12em] ${style.badge}`}>{row.priority || "normal"}</span>
                            {!row.read_at && <span className="rounded-full bg-[#FFF3E8] px-2 py-0.5 text-[10px] font-bold text-[#D95A00]">Unread</span>}
                          </div>
                          <h3 className="mt-2 text-sm font-semibold text-[#1F2621]">{row.title}</h3>
                          <p className="mt-1 whitespace-pre-line text-sm leading-6 text-[var(--ap-ink-soft)]">{row.message}</p>
                          <p className="mt-3 text-xs text-[#9CA3AF]">{new Date(row.created_at).toLocaleString()}</p>
                        </div>
                      </div>
                      {!row.read_at && (
                        <button type="button" onClick={() => markRead(row.id)} disabled={busyId === row.id} aria-label={`Mark ${row.title} read`} className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#D1D5DB] bg-white text-[#1F2621] disabled:opacity-50">
                          <Check size={15} aria-hidden="true" />
                        </button>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
